import React, { useState, useEffect, useRef, useCallback } from 'react';

// Avisos de mensajes entrantes para TODO el panel (antes vivían solo en Conversaciones):
// sonido, aviso flotante, notificación del sistema y datos de la campana.

const API = 'https://talku.online';
const authH = () => ({ 'Authorization': `Bearer ${localStorage.getItem('talku_token') || ''}` });
const SOCIAL_CHANNELS = ['instagram', 'messenger', 'tiktok'];
const POLL_MS = 6000;

export const AVATAR_COLORS = ['#6ee7b7','#93c5fd','#fca5a5','#fcd34d','#c4b5fd','#86efac','#fdba74','#67e8f9'];

export const initials = (name) => {
  const clean = (name || '').trim();
  if (!clean) return '#';
  if (/^\+?[\d\s()-]+$/.test(clean)) return '?';
  return clean.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

export const fmtPhone = (phone) => {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 12) return `+${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}`;
  return phone;
};

// Lo último que ESCRIBIÓ el cliente (last_message puede ser la respuesta del bot o del asesor)
export const inText = (c) => (c.last_inbound_message !== undefined ? c.last_inbound_message : c.last_message) || '';
export const inTime = (c) => c.last_inbound_at || c.last_message_at;

export const convName = (c) => c.contact_name || fmtPhone(c.contact_phone) || 'Contacto';

export const fmtRel = (ts) => {
  if (!ts) return '';
  try {
    const s = String(ts).replace(' ', 'T');
    const d = new Date(/[+Z]/.test(s.slice(10)) ? s : s + '-05:00');
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return 'Justo ahora';
    if (diff < 60) return `Hace ${diff} min`;
    const h = Math.floor(diff / 60);
    if (h < 24) return `Hace ${h} h`;
    if (h < 48) return 'Ayer';
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const playSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0, 0.15], [1108, 0.12, 0.28]].forEach(([freq, start, end]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + end);
      osc.start(ctx.currentTime + start); osc.stop(ctx.currentTime + end);
    });
  } catch (e) {}
};

export const notifPermission = () => ('Notification' in window ? Notification.permission : 'unsupported');

// Abre un chat: si Conversaciones está montada lo abre ahí; si no, navega con el teléfono.
export const openConversation = (conv, activeNav, onNav) => {
  if (activeNav === 'Conversaciones') {
    window.dispatchEvent(new CustomEvent('talku:open-conv', { detail: { convId: conv.id } }));
  } else if (onNav) {
    onNav('Conversaciones', (conv.contact_phone || '').replace(/\D/g, '') || undefined);
  }
};

export function useInboxAlerts(activeNav, onNav) {
  const [unread, setUnread] = useState([]);   // chats con mensajes sin leer (para la campana)
  const [toasts, setToasts] = useState([]);
  const [leaving, setLeaving] = useState(() => new Set());
  const prevRef = useRef(null);               // { convId: unread_count } de la revisión anterior
  const timersRef = useRef({});
  const swRef = useRef(null);
  const unreadRef = useRef([]);
  useEffect(() => { unreadRef.current = unread; }, [unread]);
  const navRef = useRef(activeNav);
  const onNavRef = useRef(onNav);
  useEffect(() => { navRef.current = activeNav; onNavRef.current = onNav; }, [activeNav, onNav]);

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    setLeaving(prev => new Set([...prev, id]));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      setLeaving(prev => { const s = new Set(prev); s.delete(id); return s; });
    }, 260);
  }, []);

  const ingest = useCallback((convs) => {
    const list = (convs || []).filter(c => !SOCIAL_CHANNELS.includes(c.channel || ''));
    setUnread(list.filter(c => (c.unread_count || 0) > 0)
      .sort((a, b) => String(inTime(b) || '').localeCompare(String(inTime(a) || ''))));
    const prev = prevRef.current;
    const next = {};
    list.forEach(c => { next[c.id] = c.unread_count || 0; });
    prevRef.current = next;
    if (!prev) return; // primera carga: no avisar de lo que ya estaba sin leer
    const activeConv = navRef.current === 'Conversaciones' ? window.__talkuActiveConv : null;
    const fresh = list.filter(c => (c.unread_count || 0) > (prev[c.id] || 0) && c.id !== activeConv);
    if (!fresh.length) return;
    playSound();
    fresh.forEach(c => {
      const id = `t_${c.id}`;
      const name = convName(c);
      setToasts(p => [...p.filter(t => t.id !== id), { id, conv: c, name, text: inText(c), ts: inTime(c) }].slice(-4));
      clearTimeout(timersRef.current[id]);
      timersRef.current[id] = setTimeout(() => dismiss(id), 6000);
      if (notifPermission() === 'granted' && (document.hidden || !document.hasFocus())) {
        const opts = { body: inText(c) || 'Nuevo mensaje de WhatsApp', icon: '/favicon-talku.png', tag: `conv-${c.id}`, data: { convId: c.id, phone: c.contact_phone || '' }, actions: [{ action: 'dismiss', title: 'Descartar' }] };
        try {
          if (swRef.current) swRef.current.showNotification(name, opts);
          else { const n = new Notification(name, opts); n.onclick = () => { window.focus(); openConversation(c, navRef.current, onNavRef.current); n.close(); }; }
        } catch (e) {}
      }
    });
  }, [dismiss]);

  // Revisión periódica. En Conversaciones no se consulta de nuevo: esa pantalla ya consulta
  // cada 5 s y comparte el resultado con el evento 'talku:convs'.
  useEffect(() => {
    let off = false;
    const load = async () => {
      if (navRef.current === 'Conversaciones') return;
      try {
        const r = await fetch(`${API}/whatsapp/conversations`, { headers: authH() });
        if (!r.ok) return;
        const d = await r.json();
        if (!off) ingest(Array.isArray(d) ? d : (d.conversations || []));
      } catch (e) {}
    };
    load();
    const iv = setInterval(load, POLL_MS);
    const onConvs = (e) => ingest(e.detail);
    window.addEventListener('talku:convs', onConvs);
    return () => { off = true; clearInterval(iv); window.removeEventListener('talku:convs', onConvs); };
  }, [ingest]);

  // Service worker: notificaciones del sistema y clic en ellas desde cualquier pantalla.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').then(reg => { swRef.current = reg; }).catch(() => {});
    const onMsg = (e) => {
      if (e.data?.type !== 'OPEN_CONV' || navRef.current === 'Conversaciones') return; // ahí lo atiende Conversaciones
      const c = (prevRef.current && unreadRef.current.find(x => x.id === e.data.convId)) || { id: e.data.convId };
      openConversation(c, navRef.current, onNavRef.current);
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, []);
  return { unread, toasts, leaving, dismiss };
}

export function AlertToasts({ toasts, leaving, dismiss, onOpen }) {
  if (!toasts.length) return null;
  return (
    <>
      <style>{`
        .ia-stack{position:fixed;left:20px;bottom:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;}
        .ia-t{width:min(330px,calc(100vw - 40px));background:#fff;border:1px solid #EFEFEF;border-radius:8px;box-shadow:0 10px 28px rgba(0,0,0,.08),0 1px 3px rgba(0,0,0,.04);padding:12px 12px 12px 13px;pointer-events:all;animation:ia-in .3s cubic-bezier(.16,1,.3,1) both;position:relative;overflow:hidden;}
        .ia-t.out{animation:ia-out .26s ease forwards;}
        .ia-t::after{content:'';position:absolute;left:0;bottom:0;height:2px;background:#E5E7EB;animation:ia-time 6s linear forwards;}
        .ia-h{display:flex;align-items:center;gap:10px;}
        .ia-av{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12.5px;font-weight:500;color:#374151;flex-shrink:0;}
        .ia-n{font-size:13px;font-weight:500;color:#111;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ia-s{font-size:11.5px;color:#9CA3AF;margin-top:1px;display:flex;align-items:center;gap:5px;}
        .ia-s i{width:6px;height:6px;border-radius:50%;background:#22C55E;display:inline-block;}
        .ia-x{margin-left:auto;align-self:flex-start;width:24px;height:24px;border-radius:6px;border:none;background:transparent;color:#9CA3AF;font-size:17px;line-height:1;cursor:pointer;transition:background .15s,color .15s;flex-shrink:0;}
        .ia-x:hover{background:#F5F5F5;color:#111;}
        .ia-m{margin:9px 0 10px;padding:8px 10px;background:#F7F7F7;border-radius:6px;font-size:12.5px;color:#4B5563;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .ia-b{display:flex;gap:6px;}
        .ia-btn{flex:1;height:30px;border-radius:6px;font-family:inherit;font-size:12.5px;font-weight:400;cursor:pointer;transition:background .15s,transform .15s;}
        .ia-btn:active{transform:scale(.97);}
        .ia-btn.g{border:1px solid #E5E7EB;background:#fff;color:#374151;} .ia-btn.g:hover{background:#F7F7F7;}
        .ia-btn.p{border:none;background:#111;color:#fff;} .ia-btn.p:hover{background:#2B2B2B;}
        @keyframes ia-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes ia-out{to{opacity:0;transform:translateX(-12px)}}
        @keyframes ia-time{from{width:100%}to{width:0}}
      `}</style>
      <div className="ia-stack">
        {toasts.map(t => (
          <div key={t.id} className={`ia-t${leaving.has(t.id) ? ' out' : ''}`}>
            <div className="ia-h">
              <div className="ia-av" style={{ background: AVATAR_COLORS[(t.conv.id || 0) % AVATAR_COLORS.length] }}>{initials(t.name)}</div>
              <div style={{ minWidth: 0 }}>
                <div className="ia-n">{t.name}</div>
                <div className="ia-s"><i/>Nuevo mensaje · {fmtRel(t.ts)}</div>
              </div>
              <button className="ia-x" onClick={() => dismiss(t.id)} title="Cerrar">×</button>
            </div>
            {t.text && <div className="ia-m">{t.text}</div>}
            <div className="ia-b">
              <button className="ia-btn g" onClick={() => dismiss(t.id)}>Descartar</button>
              <button className="ia-btn p" onClick={() => { onOpen(t.conv); dismiss(t.id); }}>Responder</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
